// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React from 'react';
import {
  Box,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Link, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import Logo from '@/layouts/full/shared/logo/Logo';
import authBg from '@/assets/images/backgrounds/auth.jpg';

import CustomFormLabel from '@/components/forms/theme-elements/CustomFormLabel.tsx';
import CustomTextField from '@/components/forms/theme-elements/CustomTextField.tsx';
import CountrySelectAutocomplete from '@/components/forms/form-elements/autoComplete/CountrySelectAutocomplete.tsx';
import { apiService } from '@/services/api.ts';
import { useTranslation } from '@/hooks/useTranslation';

type RegistrationFormValues = {
  customerType: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  password: string;
  phoneNumber: string;
  city: string;
  address: string;
  postCode: string;
  countryCode?: any; // Store the entire country object
  evc?: string;
  vat?: string;
};

const getSchema = (t: (key: string) => string, customerTypeName?: string): yup.ObjectSchema<RegistrationFormValues> => {
  const isBusiness = customerTypeName && customerTypeName.toLowerCase().includes('business');
  const isPrivate = customerTypeName && customerTypeName.toLowerCase().includes('private');
  
  return yup.object({
    customerType: yup.string().required(t('signup.validation.customerTypeRequired')),
    companyName: isBusiness 
      ? yup.string().required(t('signup.validation.companyNameRequired'))
      : yup.string().optional(),
    firstName: isPrivate
      ? yup.string().required(t('signup.validation.firstNameRequired'))
      : yup.string().optional(),
    lastName: isPrivate
      ? yup.string().required(t('signup.validation.lastNameRequired'))
      : yup.string().optional(),
    email: yup.string().required(t('signup.validation.emailRequired')).email(t('signup.validation.emailInvalid')),
    password: yup
      .string()
      .required(t('signup.validation.passwordRequired'))
      .min(8, t('signup.validation.passwordMinLength')),
    phoneNumber: yup.string().required(t('signup.validation.phoneNumberRequired')),
    city: yup.string().required(t('signup.validation.cityRequired')),
    address: yup.string().required(t('signup.validation.addressRequired')),
    postCode: yup.string().required(t('signup.validation.postCodeRequired')),
    countryCode: yup.object().required(t('signup.validation.countryCodeRequired')).nullable(),
    evc: yup.string().optional(),
    vat: yup.string().optional(),
  });
};

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [customerTypes, setCustomerTypes] = React.useState<Array<{ id: string; name: string }>>([]);
  const [selectedCustomerType, setSelectedCustomerType] = React.useState<string>('');
  const [customerTypeName, setCustomerTypeName] = React.useState<string>('');

  const isPrivate = (customerTypeName || '').toLowerCase().includes('privat');
  const steps = React.useMemo(
    () =>
      isPrivate
        ? [t('signup.steps.account'), t('signup.steps.profile')]
        : [t('signup.steps.account'), t('signup.steps.profile'), t('signup.steps.finish')],
    [t, isPrivate]
  );

  // Load customer types on mount
  React.useEffect(() => {
    const loadCustomerTypes = async () => {
      try {
        const response = await apiService.getCustomerTypes(false); // false = no auth required
        setCustomerTypes(response.customerTypes);
      } catch (err) {
        console.error('Failed to load customer types:', err);
      }
    };
    loadCustomerTypes();
  }, []);

  const schema = React.useMemo(() => getSchema(t, customerTypeName), [t, customerTypeName]);

  const form = useForm<RegistrationFormValues>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      customerType: '',
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phoneNumber: '',
      city: '',
      address: '',
      postCode: '',
      countryCode: null,
      evc: '',
      vat: '',
    },
  });

  const {
    register,
    formState: { errors },
    getValues,
    trigger,
    setValue,
    watch,
  } = form;

  // Update schema when customer type changes
  React.useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'customerType') {
        const newTypeId = value.customerType || '';
        setSelectedCustomerType(newTypeId);
        const type = customerTypes.find(ct => ct.id === newTypeId);
        const newTypeName = type?.name || '';
        setCustomerTypeName(newTypeName);
        // Clear errors and re-validate
        form.clearErrors();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, form, customerTypes]);

  const isStepSkipped = (step: any) => skipped.has(step);

  const handleNext = async () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    // Define required fields for each step based on customer type
    const customerTypeValue = watch('customerType');
    const isBusiness = customerTypeValue && customerTypes.find(ct => ct.id === customerTypeValue)?.name.toLowerCase().includes('business');
    const isPrivate = customerTypeValue && customerTypes.find(ct => ct.id === customerTypeValue)?.name.toLowerCase().includes('private');
    
    const step0Fields: (keyof RegistrationFormValues)[] = ['customerType', 'email', 'password'];
    if (isBusiness) {
      step0Fields.push('companyName');
    } else if (isPrivate) {
      step0Fields.push('firstName', 'lastName');
    }
    
    const stepFields: Record<number, (keyof RegistrationFormValues)[]> = {
      0: step0Fields, // Account step
      1: ['countryCode', 'city', 'address', 'phoneNumber', 'postCode'], // Profile step
      2: [], // Finish step - no required fields
    };

    // Validate current step fields before proceeding
    if (stepFields[activeStep] && stepFields[activeStep].length > 0) {
      console.log('Validating fields for step', activeStep, ':', stepFields[activeStep]);
      const isValid = await trigger(stepFields[activeStep]);
      console.log('Validation result:', isValid);
      console.log('Current errors:', errors);
      console.log('Current values:', getValues());
      
      if (!isValid) {
        console.log('Validation failed, not proceeding');
        return; // Don't proceed if validation fails
      }
    }

    // If it's the last step, submit the form
    if (activeStep === steps.length - 1) {
      await onSubmit();
      return;
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const onSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    
    try {
      const formData = getValues();
      const customerTypeValue = formData.customerType;
      const isBusiness = customerTypeValue && customerTypes.find(ct => ct.id === customerTypeValue)?.name.toLowerCase().includes('business');
      
      // Prepare API data based on customer type
      const apiData: any = {
        email: formData.email,
        password: formData.password,
        phoneNumber: formData.phoneNumber,
        city: formData.city,
        address: formData.address,
        postCode: formData.postCode,
        countryCode: formData.countryCode?.code?.toLowerCase() || '',
        customerType: formData.customerType,
      };

      // Add fields based on customer type
      if (isBusiness) {
        apiData.companyName = formData.companyName;
        if (formData.firstName) apiData.firstName = formData.firstName;
        if (formData.lastName) apiData.lastName = formData.lastName;
      } else {
        apiData.firstName = formData.firstName;
        apiData.lastName = formData.lastName;
        if (formData.companyName) apiData.companyName = formData.companyName;
      }

      // Add optional fields
      if (formData.vat) apiData.vatNumber = formData.vat;
      if (formData.evc) apiData.evcNumber = formData.evc;
      
      console.log('Sending registration data:', apiData);
      const response = await apiService.register(apiData);
      console.log('Registration response:', response);
      
      // Check if response has the expected structure
      if (!response || !response.user || !response.token) {
        throw new Error('Invalid response from server');
      }
      
      // Save user data and token to localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      
      // Send verification email - only redirect if successful
      try {
        await apiService.sendCustomerVerification(formData.email);
        // Navigate to verification page only after successful verification email
        navigate(`/verification?email=${encodeURIComponent(formData.email)}`, { replace: true });
      } catch (verificationError) {
        console.error('Failed to send verification email:', verificationError);
        // Show error but don't redirect - user is registered but verification failed
        setSubmitError(t('signup.messages.registrationSuccessful'));
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setSubmitError(err?.message || t('signup.messages.errorDuringRegistration'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleSteps = (step: any) => {
    const customerTypeValue = selectedCustomerType || watch('customerType');
    const selectedType = customerTypes.find(ct => ct.id === customerTypeValue);
    const isBusiness = selectedType?.name.toLowerCase().includes('business');
    const isPrivate = selectedType?.name.toLowerCase().includes('private');
    
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Customer Type Selection */}
            <CustomFormLabel htmlFor="customerType">{t('signup.labels.customerType')}</CustomFormLabel>
            <FormControl fullWidth error={!!errors.customerType}>
              <InputLabel id="customerType-label">{t('signup.labels.customerType')}</InputLabel>
              <Select
                labelId="customerType-label"
                id="customerType"
                value={watch('customerType') || ''}
                label={t('signup.labels.customerType')}
                {...register('customerType')}
                onChange={(e) => {
                  setValue('customerType', e.target.value);
                  setSelectedCustomerType(e.target.value);
                  // Clear fields when type changes
                  const newType = customerTypes.find(ct => ct.id === e.target.value);
                  const newIsBusiness = newType?.name.toLowerCase().includes('business');
                  const newIsPrivate = newType?.name.toLowerCase().includes('private');
                  
                  if (newIsBusiness) {
                    setValue('firstName', '');
                    setValue('lastName', '');
                  } else if (newIsPrivate) {
                    setValue('companyName', '');
                    setValue('vat', ''); // Clear VAT when switching to Private
                  }
                }}
              >
                {customerTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.customerType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.customerType.message}
                </Typography>
              )}
            </FormControl>

            {/* Company Name - only for Business */}
            {isBusiness && (
              <>
                <CustomFormLabel htmlFor="companyName">{t('signup.labels.companyName')}</CustomFormLabel>
                <CustomTextField 
                  id="companyName" 
                  variant="outlined" 
                  fullWidth
                  error={!!errors.companyName}
                  helperText={errors.companyName?.message}
                  {...register('companyName')}
                />
              </>
            )}

            {/* First Name and Last Name - only for Private */}
            {isPrivate && (
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <CustomFormLabel htmlFor="firstName">{t('signup.labels.firstName')}</CustomFormLabel>
                  <CustomTextField 
                    id="firstName" 
                    variant="outlined" 
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    {...register('firstName')}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <CustomFormLabel htmlFor="lastName">{t('signup.labels.lastName')}</CustomFormLabel>
                  <CustomTextField 
                    id="lastName" 
                    variant="outlined" 
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    {...register('lastName')}
                  />
                </Box>
              </Stack>
            )}
            <CustomFormLabel htmlFor="email">{t('signup.labels.email')}</CustomFormLabel>
            <CustomTextField 
              id="email" 
              variant="outlined" 
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email')}
            />
            <CustomFormLabel htmlFor="password">{t('signup.labels.password')}</CustomFormLabel>
            <CustomTextField 
              id="password" 
              type="password"
              variant="outlined" 
              fullWidth
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password')}
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <CustomFormLabel htmlFor="countryCode">{t('signup.labels.countryCode')}</CustomFormLabel>
            <CountrySelectAutocomplete 
              value={watch('countryCode')}
              onChange={(value) => {
                console.log('Country changed:', value);
                setValue('countryCode', value);
                console.log('Set countryCode to:', value);
              }}
              error={!!errors.countryCode}
              helperText={typeof errors.countryCode?.message === 'string' ? errors.countryCode.message : ''}
            />
            <CustomFormLabel htmlFor="city">{t('signup.labels.city')}</CustomFormLabel>
            <CustomTextField 
              id="city" 
              variant="outlined" 
              fullWidth
              error={!!errors.city}
              helperText={errors.city?.message}
              {...register('city')}
            />
            <CustomFormLabel htmlFor="address">{t('signup.labels.address')}</CustomFormLabel>
            <CustomTextField 
              id="address" 
              variant="outlined" 
              fullWidth
              error={!!errors.address}
              helperText={errors.address?.message}
              {...register('address')}
            />
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <CustomFormLabel htmlFor="phoneNumber">
                  {t('signup.labels.phoneNumber')}
                </CustomFormLabel>
                <CustomTextField 
                  id="phoneNumber" 
                  variant="outlined" 
                  fullWidth
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  {...register('phoneNumber')}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomFormLabel htmlFor="postCode">{t('signup.labels.postCode')}</CustomFormLabel>
                <CustomTextField 
                  id="postCode" 
                  variant="outlined" 
                  fullWidth
                  error={!!errors.postCode}
                  helperText={errors.postCode?.message}
                  {...register('postCode')}
                />
              </Box>
            </Stack>
          </Box>
        );
      case 2:
        return (
          <Box pt={3}>
            <Stack direction="row" spacing={2}>
              {/* VAT - only for Business */}
              {!isPrivate && (
                <Box sx={{ flex: 1 }}>
                  <CustomFormLabel htmlFor="vat">{t('signup.labels.vat')}</CustomFormLabel>
                  <CustomTextField 
                    id="vat" 
                    variant="outlined" 
                    fullWidth
                    error={!!errors.vat}
                    helperText={errors.vat?.message}
                    {...register('vat')}
                  />
                </Box>
              )}
            </Stack>
          </Box>
        );
      default:
        break;
    }
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', overflow: 'hidden' }}>
      {/* Left side - Image (70%) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '60%',
          height: '100vh',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
          borderRadius: '0!important',
        }}
      >
        <Box
          component="img"
          src={authBg}
          alt="Auth background"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block',
            borderRadius: '0!important',
          }}
        />
      </Box>

      {/* Right side - Form (30%) */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%' },
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 2, sm: 4 },
          overflow: 'auto',
          flexShrink: 0,
        }}
      >
          <Box
            sx={{ p: 4, zIndex: 1, width: '100%', my: 4 }}
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <Logo />
            </Box>
            <Box width="100%" mt={6}>
              <Stepper activeStep={activeStep}>
                {steps.map((label, index) => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: {
                    optional?: React.ReactNode;
                  } = {};
                  if (isStepSkipped(index)) {
                    stepProps.completed = false;
                  }

                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              {activeStep === steps.length ? (
                <>
                  <Stack spacing={2} mt={3}>
                    <Alert variant="filled" severity="success">
                      {t('signup.messages.allStepsCompleted')}
                    </Alert>

                    <Box textAlign="right">
                      <Button
                        onClick={handleReset}
                        variant="contained"
                        color="error"
                      >
                        {t('signup.buttons.reset')}
                      </Button>
                    </Box>
                  </Stack>
                </>
              ) : (
                <>
                  {submitError ? (
                    <Alert variant="filled" style={{ marginTop: '16px' }} severity="error">
                      {submitError}
                    </Alert>
                  ) : null}
                  <Box mt={6}>{handleSteps(activeStep)}</Box>

                  <Box display="flex" flexDirection="row" mt={3}>
                    <Button
                      color="inherit"
                      variant="contained"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      {t('signup.buttons.back')}
                    </Button>
                    <Box flex="1 1 auto" />

                    <Button
                      onClick={handleNext}
                      variant="contained"
                      color={
                        activeStep === steps.length - 1
                          ? 'success'
                          : 'secondary'
                      }
                      disabled={isSubmitting}
                    >
                      {isSubmitting 
                        ? t('signup.buttons.registering') 
                        : activeStep === steps.length - 1 
                          ? t('signup.buttons.finish') 
                          : t('signup.buttons.next')
                      }
                    </Button>
                  </Box>
                </>
              )}
            </Box>
            <Stack direction="row" spacing={1} mt={3}>
              <Typography color="textSecondary" variant="h6" fontWeight="400">
                {t('signup.messages.alreadyHaveAccount')}
              </Typography>
              <Typography
                component={Link}
                to="/"
                fontWeight="600"
                sx={{
                  textDecoration: 'none',
                  color: 'primary.main',
                }}
              >
                {t('signup.buttons.signIn')}
              </Typography>
            </Stack>
          </Box>
      </Box>
    </Box>
  );
};

export default Signup;
